/**
 * The entity a route module's loader already has (web plan W4.4).
 *
 * An entity route (src/routes/{brand,shop,store,promotion}.tsx) renders the
 * existing detail page with the entity its server loader read through the
 * SEO server: the body (H1, breadcrumbs, text, the entity's own offers and
 * stores) is in the server HTML, and the render that hydrates it shows the
 * same entity. With a preload:
 *
 * - `useEntityPage` answers `ok` with it while Apollo has no answer of its
 *   own, and skips its detail queries unless `refresh` (a signed-in viewer,
 *   `useViewerState`: the anonymous server copy has no `isFavorite` /
 *   `isSubscribed` of theirs, so their own copy replaces it once it lands);
 * - `<PageHead/>` renders nothing: the route's `meta` owns the head.
 *
 * Null everywhere else (the SPA routes, an in-app navigation): pages load and
 * title themselves as before.
 */
import { createContext, useContext } from 'react';
import type { EntityRef } from '@/seo/urls';

export interface EntityPreload {
  /** The detail-query data (`brandById`, …) the server rendered. */
  item: EntityRef;
  /** Also load the viewer's own copy (signed in: viewer-specific fields). */
  refresh: boolean;
}

export const EntityPreloadContext = createContext<EntityPreload | null>(null);

export function useEntityPreload(): EntityPreload | null {
  return useContext(EntityPreloadContext);
}
